import React, { memo, useRef, useEffect, useState } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';
import { Product } from '@/services/productService';
import { ProductCard } from './ProductCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useFetchProductsInfinite } from '@/hooks/useFetchProducts';
import { useProductstableTranslations } from '@/hooks/useProductstableTranslations';
import { logDebug } from '@/utils/errorHandler';

interface ProductGridProps {
  products: Product[];
  loading: boolean;
  error: string | null;
  filters: Record<string, any>;
  onFilterChange: (columnId: string, value: any) => void;
  onClearFilters: () => void;
  table: any;
  columns: any[];
  uniqueCategories: any[];
  uniqueTags: string[];
  uniqueBrands: string[];
  families: any[];
  isFamiliesLoading: boolean;
  // Optional infinite scroll functions
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

// Memoized product card component with proper spacing
const MemoizedProductCard = memo(
  ({ product, style }: { product: Product; style: React.CSSProperties }) => {
    // Adjust style to add padding for spacing between items
    const adjustedStyle = {
      ...style,
      padding: '0.75rem',
      boxSizing: 'border-box' as const,
    };

    return (
      <div style={adjustedStyle}>
        <ProductCard product={product} />
      </div>
    );
  }
);

export function ProductGrid({
  filters,
  products: passedProducts,
  loading: passedLoading,
  error: passedError,
  onFilterChange,
  onClearFilters,
  table,
  columns,
  uniqueCategories,
  uniqueTags,
  uniqueBrands,
  families,
  isFamiliesLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: ProductGridProps) {
  // i18n Step 1: Initialize translation hook
  const i18n = useProductstableTranslations();

  // Config only used for technical configuration, not user-facing text

  const {
    data,
    isLoading,
    error,
    fetchNextPage: useFetchProductsInfiniteFetchNextPage,
    hasNextPage: useFetchProductsInfiniteHasNextPage,
    isFetchingNextPage: useFetchProductsInfiniteIsFetchingNextPage,
  } = useFetchProductsInfinite(
    filters,
    // Only fetch when products are not passed as props (standalone mode) or when empty array is passed
    passedProducts === undefined || passedProducts.length === 0
  );

  // Use passed props if they exist and are not empty, otherwise use values from useFetchProductsInfinite
  const isLoadingData =
    passedLoading !== undefined && passedProducts && passedProducts.length > 0
      ? passedLoading
      : isLoading;
  const errorMessage =
    passedError !== undefined && passedProducts && passedProducts.length > 0 ? passedError : error;

  // Use passed infinite scroll functions if available, otherwise use internal ones
  const finalFetchNextPage = fetchNextPage || useFetchProductsInfiniteFetchNextPage;
  const finalHasNextPage =
    hasNextPage !== undefined ? hasNextPage : useFetchProductsInfiniteHasNextPage;
  const finalIsFetchingNextPage =
    isFetchingNextPage !== undefined
      ? isFetchingNextPage
      : useFetchProductsInfiniteIsFetchingNextPage;

  const productsToRender = React.useMemo(() => {
    // If products are passed directly and not empty, use them (standalone ProductGrid scenario)
    if (passedProducts !== undefined && passedProducts.length > 0) {
      return passedProducts;
    }

    // Otherwise use data from useFetchProductsInfinite (ProductsPage grid scenario or standalone)
    if (!data) {
      return [];
    }

    const result = data.pages.flatMap((page: any) => {
      // if the page is an array of products, use it directly
      // if the page is a paginated response object, use page.results
      if (Array.isArray(page)) {
        return page;
      } else if (page && page.results && Array.isArray(page.results)) {
        return page.results;
      } else {
        logDebug('Unexpected page structure in ProductGrid', {
          page,
          pageType: typeof page,
          hasResults: !!(page && page.results),
          isArray: Array.isArray(page),
          pageKeys: page ? Object.keys(page) : [],
        });
        return [];
      }
    });

    return result;
  }, [data, passedProducts]);

  // Use products directly from server-side filtering (no client-side filtering needed)
  // The useFetchProductsInfinite hook already handles all filtering on the server side
  const filteredProducts = productsToRender;

  // Grid container ref and dimensions
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(1000); // Default width
  const [containerHeight, setContainerHeight] = useState(600); // Default height
  const rowHeight = 320; // Keep consistent row height

  // Update dimensions on mount and window resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        // Use requestAnimationFrame to avoid forced reflows during render
        requestAnimationFrame(() => {
          if (containerRef.current) {
            const width = containerRef.current.offsetWidth;
            // Calculate height based on visible rows needed (avoid partial rows)
            const columnCount = getColumnCount(width);
            const rowCount = Math.ceil(filteredProducts.length / columnCount);
            // Height calculation - 2 rows visible initially, more as needed
            const visibleRows = Math.min(rowCount, 12);
            const height = visibleRows * rowHeight;

            setContainerWidth(width);
            setContainerHeight(height);
          }
        });
      }
    };

    // Initial measurement with delay to avoid mount interference
    const timeoutId = setTimeout(updateDimensions, 0);

    // Debounced resize handler to avoid excessive calls
    let resizeTimer: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateDimensions, 150); // 150ms debounce
    };

    // Add resize listener
    window.addEventListener('resize', debouncedResize);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [filteredProducts.length]);

  // Calculate columns based on container width - more conservative to prevent overflow
  const getColumnCount = (width: number) => {
    if (width < 640) return 1; // xs - single column on very small screens
    if (width < 768) return 2; // sm
    if (width < 1024) return 3; // md
    if (width < 1280) return 4; // lg
    return 5; // xl and above
  };

  const columnCount = getColumnCount(containerWidth);
  // Calculate column width with safety margin
  const columnWidth = Math.floor((containerWidth - 16) / columnCount);
  const rowCount = Math.ceil(filteredProducts.length / columnCount);

  if (isLoadingData) {
    return (
      <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {Array.from({ length: 10 }).map((_, index) => (
          <div key={index} className="flex h-full flex-col">
            <Skeleton className="mb-2 aspect-square w-full" />
            <Skeleton className="mb-1 h-4 w-2/3" />
            <Skeleton className="mb-1 h-3 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        ))}
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="flex items-center justify-center p-8 text-center">
        <div className="max-w-md">
          <h3 className="mb-2 text-lg font-medium">{i18n.getErrorText('loadProducts')}</h3>
          <p className="text-sm text-slate-500">
            {errorMessage instanceof Error
              ? errorMessage.message
              : errorMessage || i18n.getGridText('errorFallback')}
          </p>
        </div>
      </div>
    );
  }

  if (filteredProducts.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-center">
        <div className="max-w-md">
          <h3 className="mb-2 text-lg font-medium">{i18n.getStateText('emptyTitle')}</h3>
          <p className="text-sm text-slate-500">{i18n.getStateText('emptyDescription')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="size-full px-2 py-1" ref={containerRef}>
      <Grid
        columnCount={columnCount}
        columnWidth={columnWidth}
        height={containerHeight}
        rowCount={rowCount}
        rowHeight={rowHeight}
        width={containerWidth}
        overscanRowCount={1}
        className="scrollbar-hide react-window-grid"
        aria-label={i18n.getStateText('tableSummary')}
      >
        {({ columnIndex, rowIndex, style }) => {
          const idx = rowIndex * columnCount + columnIndex;
          if (idx >= filteredProducts.length) return null;

          const product = filteredProducts[idx];
          if (!product) return null;

          return <MemoizedProductCard style={style} product={product} />;
        }}
      </Grid>

      {/* Load More Button */}
      {finalHasNextPage && (
        <div className="mb-4 mt-6 flex justify-center">
          <button
            onClick={() => finalFetchNextPage()}
            disabled={finalIsFetchingNextPage}
            className="rounded-lg bg-primary px-6 py-3 text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {finalIsFetchingNextPage ? i18n.getGridText('loading') : i18n.getGridText('loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}

// Inject a CSS rule to fix React Window scrollbar issues
if (typeof document !== 'undefined') {
  const styleId = 'product-grid-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Hide all horizontal scrollbars */
      .react-window-grid::-webkit-scrollbar {
        display: none !important;
      }
      .react-window-grid {
        scrollbar-width: none !important;
      }
      
      /* Ensure no horizontal scrolling anywhere in the products area */
      #root, html, body, .app-main, .products-page, .products-container {
        max-width: 100vw !important;
      }
    `;
    document.head.appendChild(style);
  }
}
